/**
 * QL-417 — product costs of one portfolio, advisor or public. Keys live in the
 * portfolio snapshot key maps, under "snapshot", so an `allForPortfolio`
 * invalidation refreshes them, and the snapshot invalidation helpers also
 * refresh them on typed refresh hints that regenerate the "portfolio" snapshot.
 */

import type { ProductCostsData, ProductCostsSource } from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import * as advisorApi from "@/lib/api/flow-instances/portfolio-snapshots";
import * as publicApi from "@/lib/api/public-flow/portfolio-snapshots";
import { usePublicSession } from "@/lib/context/public-session-context";
import { portfolioSnapshotQueryKeys } from "@/lib/hooks/use-flow-instances/use-portfolio-snapshots";
import { publicPortfolioSnapshotQueryKeys } from "@/lib/hooks/use-public-flow/use-goal-snapshots";

export function usePortfolioProductCosts(
  portfolioId: string | null,
  source: ProductCostsSource,
  language: string,
): { productCosts: ProductCostsData | null; isLoading: boolean } {
  const session = usePublicSession();
  const queryKey = session.isPublic
    ? publicPortfolioSnapshotQueryKeys.productCosts(portfolioId, source, language)
    : portfolioSnapshotQueryKeys.productCosts(portfolioId, source, language);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      session.isPublic
        ? publicApi.getPublicPortfolioProductCosts(
            session.sessionToken,
            portfolioId!,
            source,
            language,
          )
        : advisorApi.getPortfolioProductCosts(portfolioId!, source, language),
    enabled: !!portfolioId,
  });

  return { productCosts: data ?? null, isLoading };
}
